import React, { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  FileEdit, ChevronDown, Save, UploadCloud, Rocket, Plus, Trash2, ArrowUp, ArrowDown,
  Eye, EyeOff, CheckCircle2,
  Activity, BarChart3, Target, ShieldCheck, Trophy, Users, GraduationCap, Zap, Award,
  TrendingUp, Medal, Star, Flame, Crosshair, Dumbbell, ClipboardCheck,
} from "lucide-react";

const ICONS = { Activity, BarChart3, Target, ShieldCheck, Trophy, Users, GraduationCap, Zap, Award, TrendingUp, Medal, Star, Flame, Crosshair, Dumbbell, Rocket, Eye, ClipboardCheck };
const Icon = ({ name, className }) => { const C = ICONS[name] || Star; return <C className={className} />; };

const LABELS = {
  overline: "Overline / Label", title: "Title", subtitle: "Subtitle", body: "Body Copy",
  description: "Description", tagline: "Tagline", image_url: "Image", cta_text: "Button Text",
  cta_url: "Button URL", secondary_text: "Secondary Button Text", secondary_url: "Secondary Button URL",
  seo_title: "SEO Title", meta_description: "Meta Description",
};
const isTextarea = (f) => f.includes("body") || f === "subtitle" || f === "description" || f === "meta_description";

export default function AdminSiteContent() {
  const [tab, setTab] = useState("sections");
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/site-content").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8 max-w-4xl">
        <div className="flex items-center gap-2 label-overline text-[#E63946]"><FileEdit className="w-4 h-4" /> Content</div>
        <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-tighter mt-2">Site Content</h1>
        <p className="text-sm text-slate-500 mt-2">Edit public-facing copy, homepage language, the 6 Pillars ribbon and FAQ. Save a draft, then Publish to go live.</p>

        <div className="flex gap-1 border-b border-slate-200 mt-6 overflow-x-auto">
          {[["sections", "Pages & Sections"], ["pillars", "6 Pillars"], ["faq", "FAQ"]].map(([k, l]) => (
            <button key={k} data-testid={`sc-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-wider border-b-2 -mb-px whitespace-nowrap ${tab === k ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400"}`}>{l}</button>
          ))}
        </div>

        {!data ? <div className="mt-8 text-sm text-slate-400">Loading…</div> : (
          <div className="mt-6">
            {tab === "sections" && <SectionsTab data={data} reload={load} />}
            {tab === "pillars" && <PillarsTab data={data.pillars} iconOptions={data.icon_options} reload={load} />}
            {tab === "faq" && <FaqTab data={data.faq} reload={load} />}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

/* ---------------- Sections ---------------- */
function SectionsTab({ data, reload }) {
  const groups = ["Homepage", "Pages", "Global"];
  const [open, setOpen] = useState(null);
  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <div key={g}>
          <div className="label-overline text-slate-400 mb-2">{g}</div>
          <div className="space-y-2">
            {data.sections.filter((s) => s.group === g).map((s) => (
              <SectionRow key={s.key} section={s} open={open === s.key} onToggle={() => setOpen(open === s.key ? null : s.key)} reload={reload} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function SectionRow({ section, open, onToggle, reload }) {
  const initial = { ...section.defaults, ...(section.published || {}), ...(section.draft || {}) };
  const [vals, setVals] = useState(initial);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  useEffect(() => { setVals({ ...section.defaults, ...(section.published || {}), ...(section.draft || {}) }); }, [section]);

  const set = (f, v) => setVals((x) => ({ ...x, [f]: v }));
  const saveDraft = async () => {
    setBusy(true);
    try { await api.put(`/admin/site-content/${section.key}`, vals); toast.success("Draft saved"); await reload(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const publish = async () => {
    setBusy(true);
    try { await api.put(`/admin/site-content/${section.key}`, vals); await api.post(`/admin/site-content/${section.key}/publish`); toast.success("Published — live now"); await reload(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); set("image_url", data.url); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={`sc-section-${section.key}`}>
      <button onClick={onToggle} data-testid={`sc-section-toggle-${section.key}`} className="w-full flex items-center gap-3 p-3 text-left">
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-sm truncate">{section.label}</div>
          {section.note && <div className="text-[11px] text-slate-400 truncate">{section.note}</div>}
        </div>
        {section.has_draft && <span className="text-[9px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm shrink-0">Draft</span>}
        {section.is_published && <span className="text-[9px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm shrink-0 inline-flex items-center gap-0.5"><CheckCircle2 className="w-3 h-3" /> Live</span>}
      </button>
      {open && (
        <div className="border-t border-slate-100 p-4 space-y-3">
          {section.fields.map((f) => (
            <div key={f}>
              <label className="label-overline text-[10px]">{LABELS[f] || f}</label>
              {f === "image_url" ? (
                <div className="mt-1">
                  {vals[f] && <img src={vals[f]} alt="" className="w-full h-28 object-cover rounded-sm border border-slate-200 mb-2" />}
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
                  <button data-testid={`sc-upload-${section.key}`} onClick={() => fileRef.current.click()} className="inline-flex items-center gap-2 border border-slate-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><UploadCloud className="w-3.5 h-3.5" /> Upload Image</button>
                </div>
              ) : isTextarea(f) ? (
                <textarea data-testid={`sc-field-${section.key}-${f}`} value={vals[f] || ""} onChange={(e) => set(f, e.target.value)} rows={3} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              ) : (
                <input data-testid={`sc-field-${section.key}-${f}`} value={vals[f] || ""} onChange={(e) => set(f, e.target.value)} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              )}
            </div>
          ))}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button data-testid={`sc-save-${section.key}`} disabled={busy} onClick={saveDraft} className="inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50"><Save className="w-4 h-4" /> Save Draft</button>
            <button data-testid={`sc-publish-${section.key}`} disabled={busy} onClick={publish} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50"><Rocket className="w-4 h-4" /> Publish</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Pillars ---------------- */
function PillarsTab({ data, iconOptions, reload }) {
  const [items, setItems] = useState(data.draft || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setItems(data.draft || []); }, [data]);

  const set = (i, k, v) => setItems((arr) => arr.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= items.length) return; const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; setItems(n.map((x, idx) => ({ ...x, order: idx }))); };
  const add = () => setItems((arr) => [...arr, { id: `pillar_${Date.now()}`, title: "New Pillar", description: "", icon: "Star", order: arr.length, visible: true }]);
  const remove = (i) => setItems((arr) => arr.filter((_, idx) => idx !== i));
  const payload = () => ({ items: items.map((x, idx) => ({ ...x, order: idx })) });
  const saveDraft = async () => { setBusy(true); try { await api.put("/admin/site-content/home_pillars", payload()); toast.success("Draft saved"); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  const publish = async () => { setBusy(true); try { await api.put("/admin/site-content/home_pillars", payload()); await api.post("/admin/site-content/home_pillars/publish"); toast.success("Published — live now"); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">The pillar ribbon shown on the homepage.</p>
        <button data-testid="pillar-add" onClick={add} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Plus className="w-4 h-4" /> Add Pillar</button>
      </div>
      <div className="space-y-2" data-testid="pillars-list">
        {items.map((p, i) => (
          <div key={p.id} data-testid={`pillar-${i}`} className={`bg-white border rounded-sm p-3 ${p.visible ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
            <div className="flex items-start gap-3">
              <div className="flex flex-col shrink-0">
                <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-[#0A192F] disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-slate-400 hover:text-[#0A192F] disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
              </div>
              <span className="w-9 h-9 rounded-sm bg-[#E63946]/10 text-[#E63946] grid place-items-center shrink-0"><Icon name={p.icon} className="w-4 h-4" /></span>
              <div className="min-w-0 flex-1 space-y-2">
                <input data-testid={`pillar-title-${i}`} value={p.title} onChange={(e) => set(i, "title", e.target.value)} placeholder="Pillar title" className="w-full px-3 py-1.5 border border-slate-300 rounded-sm text-sm font-semibold" />
                <textarea data-testid={`pillar-desc-${i}`} value={p.description} onChange={(e) => set(i, "description", e.target.value)} rows={2} placeholder="Description" className="w-full px-3 py-1.5 border border-slate-300 rounded-sm text-sm" />
                <div className="flex items-center gap-2">
                  <select data-testid={`pillar-icon-${i}`} value={p.icon} onChange={(e) => set(i, "icon", e.target.value)} className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs">
                    {iconOptions.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                  <button data-testid={`pillar-visible-${i}`} onClick={() => set(i, "visible", !p.visible)} className={`inline-flex items-center gap-1 px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider rounded-sm ${p.visible ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{p.visible ? <><Eye className="w-3.5 h-3.5" /> Shown</> : <><EyeOff className="w-3.5 h-3.5" /> Hidden</>}</button>
                  <div className="flex-1" />
                  <button data-testid={`pillar-delete-${i}`} onClick={() => remove(i)} className="text-slate-400 hover:text-[#E63946]"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mt-4">
        <button data-testid="pillars-save" disabled={busy} onClick={saveDraft} className="inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50"><Save className="w-4 h-4" /> Save Draft</button>
        <button data-testid="pillars-publish" disabled={busy} onClick={publish} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50"><Rocket className="w-4 h-4" /> Publish</button>
      </div>
    </div>
  );
}

/* ---------------- FAQ ---------------- */
function FaqTab({ data, reload }) {
  const [state, setState] = useState(data.draft || { overline: "FAQ", title: "", subtitle: "", items: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setState(data.draft || { overline: "FAQ", title: "", subtitle: "", items: [] }); }, [data]);

  const setItem = (i, k, v) => setState((s) => ({ ...s, items: s.items.map((x, idx) => idx === i ? { ...x, [k]: v } : x) }));
  const move = (i, d) => { const j = i + d; if (j < 0 || j >= state.items.length) return; const n = [...state.items]; [n[i], n[j]] = [n[j], n[i]]; setState((s) => ({ ...s, items: n })); };
  const add = () => setState((s) => ({ ...s, items: [...s.items, { id: `q_${Date.now()}`, question: "", answer: "", order: s.items.length }] }));
  const remove = (i) => setState((s) => ({ ...s, items: s.items.filter((_, idx) => idx !== i) }));
  const payload = () => ({ ...state, items: state.items.map((x, idx) => ({ ...x, order: idx })) });
  const saveDraft = async () => { setBusy(true); try { await api.put("/admin/site-content/faq", payload()); toast.success("Draft saved"); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };
  const publish = async () => { setBusy(true); try { await api.put("/admin/site-content/faq", payload()); await api.post("/admin/site-content/faq/publish"); toast.success("Published — live now"); await reload(); } catch (e) { toast.error(e.message); } finally { setBusy(false); } };

  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        <div><label className="label-overline text-[10px]">Overline</label><input data-testid="faq-overline" value={state.overline || ""} onChange={(e) => setState({ ...state, overline: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
        <div><label className="label-overline text-[10px]">Title</label><input data-testid="faq-title" value={state.title || ""} onChange={(e) => setState({ ...state, title: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
      </div>
      <div><label className="label-overline text-[10px]">Subtitle</label><input data-testid="faq-subtitle" value={state.subtitle || ""} onChange={(e) => setState({ ...state, subtitle: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>

      <div className="flex items-center justify-between pt-2">
        <div className="label-overline text-slate-400">Questions</div>
        <button data-testid="faq-add" onClick={add} className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"><Plus className="w-4 h-4" /> Add Question</button>
      </div>
      <div className="space-y-2" data-testid="faq-list">
        {state.items.map((q, i) => (
          <div key={q.id} data-testid={`faq-item-${i}`} className="bg-white border border-slate-200 rounded-sm p-3 flex items-start gap-3">
            <div className="flex flex-col shrink-0">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-[#0A192F] disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === state.items.length - 1} className="text-slate-400 hover:text-[#0A192F] disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
            </div>
            <div className="min-w-0 flex-1 space-y-2">
              <input data-testid={`faq-q-${i}`} value={q.question} onChange={(e) => setItem(i, "question", e.target.value)} placeholder="Question" className="w-full px-3 py-1.5 border border-slate-300 rounded-sm text-sm font-semibold" />
              <textarea data-testid={`faq-a-${i}`} value={q.answer} onChange={(e) => setItem(i, "answer", e.target.value)} rows={2} placeholder="Answer" className="w-full px-3 py-1.5 border border-slate-300 rounded-sm text-sm" />
            </div>
            <button data-testid={`faq-delete-${i}`} onClick={() => remove(i)} className="text-slate-400 hover:text-[#E63946] shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 pt-1">
        <button data-testid="faq-save" disabled={busy} onClick={saveDraft} className="inline-flex items-center gap-2 border border-[#0A192F] text-[#0A192F] px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50"><Save className="w-4 h-4" /> Save Draft</button>
        <button data-testid="faq-publish" disabled={busy} onClick={publish} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-50"><Rocket className="w-4 h-4" /> Publish</button>
      </div>
    </div>
  );
}
